// frontend/src/components/auth/AuthUtils.js
import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useAuth } from './AuthProvider';
import toast from 'react-hot-toast';

// Protected Route Component
export const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Component (redirects to dashboard if authenticated)
export const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Password Strength Indicator Component
export const PasswordStrengthIndicator = ({ password }) => {
  const getStrength = (pwd) => {
    let score = 0;
    if (pwd.length >= 8) score += 1;
    if (/[a-z]/.test(pwd)) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/\d/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
    return score;
  };

  const strength = getStrength(password);
  const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColors = ['bg-danger', 'bg-danger', 'bg-warning', 'bg-info', 'bg-success'];

  if (!password) return null;

  return (
    <div className="mt-2">
      <div className="flex space-x-1 mb-1">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded ${
              i < strength ? strengthColors[strength - 1] : 'bg-surface-sunken'
            }`}
          />
        ))}
      </div>
      <p className={`text-xs ${
        strength <= 2 ? 'text-danger' : strength <= 3 ? 'text-warning' : 'text-success'
      }`}>
        Password strength: {strengthLabels[strength - 1] || 'Very Weak'}
      </p>
    </div>
  );
};

// Auth Guard Hook for route protection
export const useAuthGuard = (requiredRole = null) => {
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    } else if (!loading && requiredRole && user?.user_type !== requiredRole) {
      navigate('/unauthorized');
    }
  }, [loading, isAuthenticated, user, requiredRole, navigate]);

  return { user, isAuthenticated, loading };
};

// Logout Confirmation Component
export const LogoutConfirmation = ({ isOpen, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
        <h3 className="text-lg font-semibold text-ink mb-2">Confirm Logout</h3>
        <p className="text-ink-muted mb-6">Are you sure you want to sign out?</p>
        
        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-border-default rounded-md hover:bg-surface-sunken"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 bg-danger text-white rounded-md hover:bg-danger/80"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

// Email Verification Component (for future use)
export const EmailVerification = ({ email }) => {
  const [resendLoading, setResendLoading] = useState(false);

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      // await api.resendVerificationEmail({ email });
      toast.success('Verification email sent!');
    } catch (error) {
      toast.error('Failed to send verification email');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="bg-warning-tint border border-warning-border rounded-lg p-4">
      <div className="flex">
        <AlertCircle className="w-5 h-5 text-warning mr-3 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-medium text-warning">
            Email Verification Required
          </h3>
          <p className="text-sm text-warning mt-1">
            Please check your email ({email}) and click the verification link to activate your account.
          </p>
          <button
            onClick={handleResendVerification}
            disabled={resendLoading}
            className="mt-2 text-sm text-warning underline hover:text-warning/80 disabled:opacity-50"
          >
            {resendLoading ? 'Sending...' : 'Resend verification email'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Session Timeout Warning Component
export const SessionTimeoutWarning = ({ onExtend, onLogout }) => {
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          onLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onLogout]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  return (
    <div className="fixed top-0 left-0 right-0 bg-warning-tint border-b border-warning-border text-warning p-4 z-50 shadow-sticky" role="alert">
      <div className="max-w-4xl mx-auto flex items-center justify-between">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          <span>
            Your session will expire in {minutes}:{seconds.toString().padStart(2, '0')}
          </span>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={onExtend}
            className="bg-surface-card text-ink border border-border-strong min-h-touch px-4 rounded-control text-body font-semibold hover:bg-surface-sunken transition-colors duration-state"
          >
            Stay Logged In
          </button>
          <button
            onClick={onLogout}
            className="text-warning underline text-body hover:no-underline min-h-touch"
          >
            Logout Now
          </button>
        </div>
      </div>
    </div>
  );
};

// Role Badge Component
export const RoleBadge = ({ userType }) => {
  const roleConfig = {
    mechanic: { label: 'Mechanic', color: 'bg-info-tint text-info' },
    dealer: { label: 'Dealer', color: 'bg-info-tint text-info' },
    consumer: { label: 'Consumer', color: 'bg-success-tint text-success' },
    admin: { label: 'Admin', color: 'bg-danger-tint text-danger' }
  };

  const config = roleConfig[userType] || roleConfig.consumer;

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

// Loading Spinner Component
export const LoadingSpinner = ({ size = 'md', text = 'Loading...' }) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12'
  };

  return (
    <div className="flex flex-col items-center justify-center p-4">
      <div className={`animate-spin rounded-full border-b-2 border-accent ${sizeClasses[size]}`}></div>
      {text && <p className="mt-2 text-sm text-ink-muted">{text}</p>}
    </div>
  );
};

// Error Boundary Component for Auth
export class AuthErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Auth Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-surface-sunken">
          <div className="max-w-md w-full text-center">
            <AlertCircle className="w-12 h-12 text-danger mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-ink mb-2">
              Authentication Error
            </h2>
            <p className="text-ink-muted mb-4">
              Something went wrong with authentication. Please refresh the page or try logging in again.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-accent text-white px-4 py-2 rounded-md hover:bg-accent-hover"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}